import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Field, Text, VStack, Center, Spinner, Image } from '@chakra-ui/react';
import PasswordInput from '@/components/ui/foundation/PasswordInput';
import Logo from '@/assets/images/cyberjob_dialogflow_logo.svg';
import { toaster } from '@/components/ui/toaster';
import ActivationService from '@/services/ActivationService';

const SetPasswordPage = ({ mode = 'activation' }) => {
    const { token } = useParams();
    const navigate = useNavigate();

    const [status, setStatus] = useState('loading');
    const [userInfo, setUserInfo] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        ActivationService.verifyToken(token, mode)
            .then(data => {
                setUserInfo(data);
                setStatus('ready');
            })
            .catch(() => {
                setStatus('invalid');
            });
    }, [token]);

    const validate = () => {
        const e = {};
        if (password.length < 8) e.password = 'Password must be at least 8 characters';
        if (password !== confirmPassword) e.confirmPassword = 'Passwords do not match';
        return e;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        if (Object.keys(e).length > 0) { setErrors(e); return; }
        setErrors({});
        setIsSubmitting(true);
        try {
            if (mode === 'activation') {
                await ActivationService.setPassword(token, password);
            } else {
                await ActivationService.confirmPasswordReset(token, password);
            }
            toaster.create({ type: 'success', title: 'Password set successfully' });
            navigate('/login');
        } catch {
            toaster.create({ type: 'error', title: 'Something went wrong. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (status === 'loading') {
        return (
            <Center minH="100vh" w="100vw" bg="bg.primary">
                <VStack gap={4}>
                    <Spinner size="xl" color="blue.400" borderWidth="3px" />
                    <Text color="text.grey" fontSize={14}>Verifying link...</Text>
                </VStack>
            </Center>
        );
    }

    if (status === 'invalid') {
        return (
            <Center minH="100vh" w="100vw" bg="bg.primary">
                <Box w="400px" p={8} bg="bg.secondary" borderRadius={16}>
                    <VStack gap={6}>
                        <Text fontSize={20} color="white" fontWeight="bold" textAlign="center">
                            Link expired or invalid
                        </Text>
                        <Text color="text.grey" textAlign="center">
                            This activation link has expired or is no longer valid.
                        </Text>
                        <Button variant="primary" w="full" onClick={() => navigate('/login')}>
                            Back to login
                        </Button>
                    </VStack>
                </Box>
            </Center>
        );
    }

    return (
        <Center minH="100vh" w="100vw" bg="bg.primary">
            <Box w="400px" p={8} bg="bg.secondary" borderRadius={16}>
                <VStack gap={6} as="form" onSubmit={handleSubmit}>
                    <Image src={Logo} w="70%" mb={2} />
                    <Text fontSize={24} color="white" fontWeight="bold">Set your password</Text>
                    {userInfo && (
                        <VStack gap={1}>
                            <Text color="text.grey" textAlign="center">
                                Hello <Text as="span" color="white" fontWeight="semibold">{userInfo.firstName ?? userInfo.FirstName}</Text>,
                            </Text>
                            <Text color="text.grey" textAlign="center" fontSize={13}>
                                set a password for <Text as="span" color="blue.300">{userInfo.email ?? userInfo.Email}</Text>
                            </Text>
                        </VStack>
                    )}

                    <Field.Root required w="full" invalid={!!errors.password}>
                        <Field.Label color="white">Password <Field.RequiredIndicator /></Field.Label>
                        <PasswordInput value={password} onChange={e => setPassword(e.target.value)} />
                        {errors.password && <Field.ErrorText>{errors.password}</Field.ErrorText>}
                    </Field.Root>

                    <Field.Root required w="full" invalid={!!errors.confirmPassword}>
                        <Field.Label color="white">Confirm password <Field.RequiredIndicator /></Field.Label>
                        <PasswordInput value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                        {errors.confirmPassword && <Field.ErrorText>{errors.confirmPassword}</Field.ErrorText>}
                    </Field.Root>

                    <Button variant="primary" type="submit" w="full" loading={isSubmitting}>
                        Set password
                    </Button>
                </VStack>
            </Box>
        </Center>
    );
};

export default SetPasswordPage;
